// useReferenceMedia.js
import { useState, useCallback, useEffect } from 'react';
import referenceMediaApi, { base64ToFile } from './referenceMediaApi';

// target: 'product-item' | 'dish'. targetId is the Reference item's own _id — only meaningful
// once the item has been saved at least once (a brand-new, unsaved item has no _id yet, so the
// caller should not mount this until itemData?._id exists).
export const useReferenceMedia = (target, targetId, itemName) => {
  const [containerId, setContainerId] = useState(null);
  const [files, setFiles] = useState([]); // [{ _id, name, mtype, url }]
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    setError(null);
    try {
      const container = await referenceMediaApi.findContainer(target, targetId);
      if (container) {
        setContainerId(container._id);
        const active = (container.files || [])
          .filter(f => !f.status || f.status.active !== false)
          .map(f => ({ ...f, url: referenceMediaApi.fileUrl(container._id, f._id) }));
        setFiles(active);
      } else {
        setContainerId(null);
        setFiles([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [target, targetId]);

  useEffect(() => { load(); }, [load]);

  // picked: the array FileUploader hands back via onChange when asBase64 — each item
  // { type, name, size, file: <base64 data URL> }.
  const uploadPicked = useCallback(async (picked) => {
    if (!targetId || !picked || picked.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      let cId = containerId;
      if (!cId) {
        const container = await referenceMediaApi.ensureContainer(target, targetId, itemName || 'Медіа');
        cId = container._id;
        setContainerId(cId);
      }
      for (const item of picked) {
        const file = base64ToFile(item.file, item.name, item.type);
        await referenceMediaApi.uploadFile(cId, file);
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }, [target, targetId, itemName, containerId, load]);

  const addYoutubeVideo = useCallback(async (url, caption) => {
    if (!targetId || !url) return;
    setUploading(true);
    setError(null);
    try {
      let cId = containerId;
      if (!cId) {
        const container = await referenceMediaApi.ensureContainer(target, targetId, itemName || 'Медіа');
        cId = container._id;
        setContainerId(cId);
      }
      await referenceMediaApi.addYoutubeVideo(cId, url, { caption });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }, [target, targetId, itemName, containerId, load]);

  const removeFile = useCallback(async (fileId) => {
    if (!containerId) return;
    setError(null);
    try {
      const file = files.find(f => f._id === fileId);
      if (file && file.source === 'youtube') {
        await referenceMediaApi.removeYoutubeVideo(containerId, fileId);
      } else {
        await referenceMediaApi.removeFile(containerId, fileId);
      }
      await load();
    } catch (err) {
      setError(err.message);
    }
  }, [containerId, files, load]);

  return { files, loading, uploading, error, uploadPicked, addYoutubeVideo, removeFile };
};

export default useReferenceMedia;